import { NavSection } from "./SelectionPanel";
import { ConfigTarget, actionNeedsSetup, isAuth, isDormantAction, isFlowAction, slugOf } from "./actionSetup";

type Translate = (key: any, options?: any) => string;

export const DETAILS_SLUG = "details";

// What a section is called: the connector's own wording wins — an explicit
// ui:title, else the action name. Never schema.title, which is the Python config
// class name. Only a nameless auth action falls back to a generic label.
export const actionLabel = (a: any, t: Translate) =>
    a?.ui_schema?.["ui:title"] || a?.name || (isAuth(a) ? t("Credentials") : slugOf(a));

// The nav every configuration screen shows: the integration's own fields first,
// then its actions grouped the same way the flow map's node panel groups them —
// credentials, then the directions data can move, then anything else.
export const ADVANCED_CAT = "Advanced Config";

export function buildActionNav(
    { target, actions, t, detailsLabel, detailsSlug, detailsNeedsSetup, dormantTypes, authBadgesOnly }: {
        // Undefined before the record exists (create flows) — no "!" badges then,
        // since nothing could have been saved yet.
        target?: ConfigTarget;
        actions: any[];
        t: Translate;
        detailsLabel?: string;
        // The flow map's account panel calls its first section "general"; every
        // other screen uses DETAILS_SLUG.
        detailsSlug?: string;
        detailsNeedsSetup?: boolean;
        // Flow-action types ("pull"/"push") whose direction isn't carrying data
        // right now. Their sections stay editable as FYIs but carry no "!" badge —
        // nothing blocks the flow without them. (No "Optional" chip either: plenty
        // of other sections are optional too and aren't labeled.)
        dormantTypes?: Set<string>;
        // Whether a non-auth field is really required depends on the role the
        // Route gives this account (provider vs. destination) — a role the
        // standalone Connection page doesn't know. Only credentials are required
        // regardless, so that page badges auth actions alone; the flow map,
        // which knows the role, keeps badging flow actions too.
        authBadgesOnly?: boolean;
    },
): NavSection[] {
    // Advanced Config helpers (non-flow, non-auth) never badge: their required
    // schema fields serve an on-demand action, and nothing in the flow waits on
    // them. Same call computeNeedsSetup makes for the node badge.
    const canBadge = (a: any) => isAuth(a)
        || (!authBadgesOnly && isFlowAction(a) && !isDormantAction(a, dormantTypes));
    const toItem = (a: any) => ({
        slug: slugOf(a),
        label: actionLabel(a, t),
        needsSetup: !!target && canBadge(a) && actionNeedsSetup(target, a),
    });
    const all = actions || [];
    const groups: [string, any[]][] = [
        [t("Credentials"), all.filter(isAuth)],
        [t("Send data to Gundi"), all.filter((a) => !isAuth(a) && a.type === "pull")],
        [t("Receive data from Gundi"), all.filter((a) => !isAuth(a) && a.type === "push")],
        [t(ADVANCED_CAT), all.filter((a) => !isAuth(a) && a.type !== "pull" && a.type !== "push")],
    ];
    const nav: NavSection[] = [{
        cat: t("GENERAL"),
        items: [{ slug: detailsSlug || DETAILS_SLUG, label: detailsLabel || t("General"), needsSetup: !!detailsNeedsSetup }],
    }];
    groups.forEach(([cat, group]) => {
        if (group.length > 0) nav.push({ cat, items: group.map(toItem) });
    });
    return nav;
}

export const actionForSlug = (actions: any[], slug?: string) =>
    (actions || []).find((a) => slugOf(a) === slug);

// Marks the sections whose action has field errors from the last failed submit.
// The creation screens (New Connection, new-Route wizard) deliberately do NOT
// relocate the user, so this badge plus the toast is the whole signal that
// something is wrong in a section they may never have opened — which is why both
// screens read it from here instead of keeping their own copy.
export function flagSectionErrors(
    nav: NavSection[],
    actions: any[],
    // action id -> { field: message }
    fieldErrors: Record<string, Record<string, string>> | undefined,
): NavSection[] {
    const hasErrors = (a: any) => Object.keys(fieldErrors?.[a?.id] ?? {}).length > 0;
    return nav.map((section) => ({
        ...section,
        items: section.items.map((item) => {
            const action = (actions || []).find((a) => slugOf(a) === item.slug);
            return action && hasErrors(action) ? { ...item, needsSetup: true } : item;
        }),
    }));
}
